// src/ui/components/waveTransition.ts — Animated Wave Transition Cue (FR-INTER-002)
// Brief 1.5s animated oceanic wave divider with sub-topic label
// and non-blocking tap skip when question strategy switches.

import { tokens } from '@ui/designTokens';
import { soundEngine } from '@audio/soundEngine';

export interface WaveTransitionOptions {
  newSubTopic: string;
  domainName?: string;
  durationMs?: number; // default 1500ms (1.5s, FR-INTER-002)
  onComplete?: () => void;
}

export class WaveTransitionCue {
  private overlay: HTMLElement;
  private timer: NodeJS.Timeout | null = null;
  private onComplete?: () => void;
  private isDestroyed: boolean = false;

  constructor(options: WaveTransitionOptions) {
    this.onComplete = options.onComplete;
    const duration = options.durationMs ?? 1500;

    this.overlay = document.createElement('div');
    this.overlay.className = 'wave-transition-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(10, 25, 47, 0.72);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      z-index: 9999;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.25s ease;
      touch-action: manipulation;
    `;

    this.overlay.innerHTML = `
      <div style="position: relative; max-width: 520px; width: 90%; background: rgba(255, 253, 249, 0.98); border: 2.5px solid ${tokens.color.gildedBrass}; border-radius: 20px; padding: 32px 28px; text-align: center; box-shadow: 0 16px 48px rgba(7, 20, 36, 0.35); transform: translateY(12px); transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);">
        <!-- Animated SVG Wave Crest -->
        <div style="margin-bottom: 16px; overflow: hidden; height: 48px;">
          <svg viewBox="0 0 500 80" preserveAspectRatio="none" style="width: 100%; height: 100%;">
            <path d="M0,30 C150,70 350,0 500,30 L500,80 L0,80 Z" fill="rgba(0, 229, 255, 0.25)"></path>
            <path d="M0,45 C180,10 320,60 500,45 L500,80 L0,80 Z" fill="${tokens.color.morrisIndigo}"></path>
          </svg>
        </div>

        <div style="display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 8px;">
          🌊 Shifting Current · New Strategy
        </div>

        <h3 style="font-family: ${tokens.font.heading}; font-size: 1.65rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 10px;">
          ${options.newSubTopic}
        </h3>

        ${options.domainName ? `
          <div style="font-size: 0.95rem; color: ${tokens.color.madderCoral}; font-weight: 700; margin-bottom: 14px;">
            ${options.domainName}
          </div>
        ` : ''}

        <p style="font-size: 0.95rem; color: #4A5B73; margin: 0 0 16px;">
          Switch your problem-solving lens, Tudor! Notice what strategy this new clue requires.
        </p>

        <div style="font-size: 0.8rem; color: ${tokens.color.morrisIndigo}; opacity: 0.6; font-style: italic;">
          (Tap anywhere to dive straight in)
        </div>
      </div>
    `;

    // Non-blocking tap to dismiss immediately
    this.overlay.addEventListener('click', () => {
      soundEngine.playTap();
      this.dismiss();
    });

    // Auto-dismiss after duration (default 1.5s)
    this.timer = setTimeout(() => {
      this.dismiss();
    }, duration);

    // Fade in
    requestAnimationFrame(() => {
      this.overlay.style.opacity = '1';
      const card = this.overlay.firstElementChild as HTMLElement | null;
      if (card) {
        card.style.transform = 'translateY(0)';
      }
    });
  }

  show(target: HTMLElement = document.body): void {
    target.appendChild(this.overlay);
  }

  dismiss(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    this.overlay.style.opacity = '0';
    setTimeout(() => {
      if (this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
      }
      if (this.onComplete) {
        this.onComplete();
      }
    }, 200);
  }

  getElement(): HTMLElement {
    return this.overlay;
  }
}
